/**
 * Base fetch wrapper with CSRF token handling for Django session auth.
 *
 * All non-2xx responses are routed through the error middleware which
 * dispatches system toasts. Callers that handle errors locally (auth pages,
 * non-critical background fetches) can pass `{ silent: true }` to suppress
 * the automatic toast.
 */

import { handleApiError } from "./error-middleware";
import type { ErrorMiddlewareOptions } from "./error-middleware";
import { isTauri } from "@/lib/is-tauri";
import { CLIENT_PROTOCOL_HEADERS } from "./client-protocol";
import { desktopApiOrigin } from "@/lib/release-channel";

type FetchNetworkError = TypeError & {
  requestUrl?: string;
  cause?: unknown;
};

function getCsrfToken(): string | null {
  const match = document.cookie.match(/csrftoken=([^;]+)/);
  return match ? match[1]! : null;
}

let cachedDesktopToken: string | null = null;
let desktopTokenGeneration = 0;
async function getDesktopToken(): Promise<string | null> {
  if (cachedDesktopToken) return cachedDesktopToken;
  if (!isTauri()) return null;
  try {
    // Late import keeps the Tauri SDK out of the web bundle.
    const generation = desktopTokenGeneration;
    const { invoke } = await import("@tauri-apps/api/core");
    const token = await invoke<string | null>("get_auth_token");
    if (generation === desktopTokenGeneration) cachedDesktopToken = token;
    return token;
  } catch {
    // Transient IPC failure (e.g. early-startup race). Leave the cache
    // null so the next request retries; do not pin the cache to null.
    return null;
  }
  return cachedDesktopToken;
}

export function clearDesktopTokenCache(): void {
  desktopTokenGeneration++;
  cachedDesktopToken = null;
}

// In the bundled Tauri .app the webview is loaded from a custom
// protocol (`tauri://localhost`), so a relative `/api/v1/...` URL
// resolves against that origin and never reaches Django. In the web
// build and in `cargo tauri dev` the request goes through the Vite
// dev server's proxy (see vite.config.ts) so relative URLs work
// fine. Detect the bundled case at runtime and prepend the API host of
// the app's release channel.
const DESKTOP_API_BASE = desktopApiOrigin().replace(/\/$/, "");

export function resolveApiUrl(url: string): string {
  if (!isTauri()) return url;
  if (!DESKTOP_API_BASE) return url;
  // Absolute URLs are already fully-qualified — don't double-prefix.
  if (/^https?:\/\//i.test(url) || /^tauri:\/\//i.test(url)) return url;
  if (!url.startsWith("/")) return url;
  return `${DESKTOP_API_BASE}${url}`;
}

function wrapNetworkError(requestUrl: string, err: unknown): FetchNetworkError {
  const message =
    err instanceof Error && err.message
      ? `${err.message} (${requestUrl})`
      : `Network request failed (${requestUrl})`;
  const wrapped = new TypeError(message) as FetchNetworkError;
  wrapped.requestUrl = requestUrl;
  if (err !== wrapped) wrapped.cause = err;
  return wrapped;
}

export async function ensureCsrf(): Promise<void> {
  if (getCsrfToken()) return;
  const requestUrl = resolveApiUrl("/api/v1/auth/csrf/");
  try {
    await fetch(requestUrl, {
      credentials: "include",
    });
  } catch (err) {
    throw wrapNetworkError(requestUrl, err);
  }
}

interface RequestOptions
  extends Omit<RequestInit, "body">, ErrorMiddlewareOptions {
  body?: unknown;
  /** Recheck auth/account ownership after asynchronous auth resolution. */
  requestGuard?: () => boolean;
}

export interface ApiError {
  status: number;
  data: Record<string, unknown>;
}

/** The server answered 404: what was asked for is not there (any more). */
export function isNotFoundError(error: unknown): boolean {
  return (error as Partial<ApiError> | null)?.status === 404;
}

/** One authenticated request; non-2xx responses go through the error middleware and throw. */
async function apiFetch(
  url: string,
  options: RequestOptions,
): Promise<Response> {
  const { silent, context, body, requestGuard, ...fetchOptions } = options;
  const assertRequestScope = () => {
    if (requestGuard && !requestGuard())
      throw new Error("Request scope changed");
  };
  assertRequestScope();

  const desktopToken = await getDesktopToken();
  if (!desktopToken) await ensureCsrf();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Denker-Surface": isTauri() ? "desktop" : "web",
    ...(fetchOptions.headers as Record<string, string>),
    ...CLIENT_PROTOCOL_HEADERS,
  };

  if (desktopToken) {
    // Bearer auth via the existing CompanionTokenAuthentication on the
    // backend. CSRF is not required when token auth is in use.
    headers["Authorization"] = `Bearer ${desktopToken}`;
  } else {
    const csrfToken = getCsrfToken();
    if (csrfToken && fetchOptions.method && fetchOptions.method !== "GET") {
      headers["X-CSRFToken"] = csrfToken;
    }
  }

  let response: Response;
  const requestUrl = resolveApiUrl(url);
  assertRequestScope();
  try {
    response = await fetch(requestUrl, {
      ...fetchOptions,
      headers,
      credentials: "include",
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    // Network error (no response received)
    assertRequestScope();
    const wrapped = wrapNetworkError(requestUrl, err);
    handleApiError(wrapped, { silent, context });
    throw wrapped;
  }

  // A stale 401 must not clear credentials belonging to the next session.
  assertRequestScope();
  if (!response.ok) {
    let data: Record<string, unknown> = {};
    try {
      data = (await response.json()) as Record<string, unknown>;
    } catch {
      data = { detail: response.statusText };
    }
    assertRequestScope();
    // Desktop: a 401 against a desktop token means the token is no
    // longer valid (revoked, expired, env mismatch). Tear down the
    // bad auth state in one place — clear the in-memory cache, wipe
    // Keychain, kill the companion subprocess that's holding the bad
    // token, and notify AuthGate to drop back to the sign-in popover.
    // Without this the user sits in a permanent empty UI with every
    // store fetch silently 401-ing.
    if (response.status === 401 && desktopToken && isTauri()) {
      clearDesktopTokenCache();
      const recoveryGeneration = desktopTokenGeneration;
      const assertRecoveryScope = () => {
        assertRequestScope();
        if (recoveryGeneration !== desktopTokenGeneration)
          throw new Error("Request scope changed");
      };
      try {
        const { invoke } = await import("@tauri-apps/api/core");
        const { emit } = await import("@tauri-apps/api/event");
        assertRecoveryScope();
        await invoke("clear_auth_token").catch(() => {});
        assertRecoveryScope();
        // Companion is owner-only (one subprocess per session). Avoid
        // calling stop from mirror windows — the Rust singleton is
        // idempotent, but issuing the IPC from N webviews on every 401
        // is noise.
        const { getWindowRoleSync } = await import("@/desktop/use-window-role");
        assertRecoveryScope();
        if (getWindowRoleSync() === "owner") {
          await invoke("companion_stop").catch(() => {});
        }
        assertRecoveryScope();
        await emit("denker://auth-token-invalid").catch(() => {});
        assertRecoveryScope();
        console.warn(
          "[auth] desktop token rejected by API; cleared and signalled re-auth",
        );
      } catch {
        // If the Tauri IPC bridge itself is gone we can't recover —
        // surface the error to the caller as normal.
      }
      assertRecoveryScope();
    }
    const error: ApiError = { status: response.status, data };
    handleApiError(error, { silent, context });
    throw error;
  }
  return response;
}

export async function apiRequest<T>(
  url: string,
  options: RequestOptions = {},
): Promise<T> {
  const response = await apiFetch(url, options);
  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return {} as T;
  }

  return response.json() as Promise<T>;
}

/** A JSON request whose caller also needs the status (201 created vs 200). */
export async function apiRequestWithStatus<T>(
  url: string,
  options: RequestOptions = {},
): Promise<{ status: number; data: T }> {
  const response = await apiFetch(url, options);
  return { status: response.status, data: (await response.json()) as T };
}

/** A text body (Markdown exports and other non-JSON downloads). */
export async function apiRequestText(
  url: string,
  options: RequestOptions = {},
): Promise<string> {
  const response = await apiFetch(url, options);
  return response.text();
}
